import { useCallback, useEffect, useRef, useState } from "react";
import {
  useEditor,
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  Node,
  Editor,
  InputRule,
  PasteRule,
  type NodeViewProps,
  type JSONContent,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconClockHour5,
  IconFileArrowRight,
  IconSparkles,
} from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { cn } from "@/lib/utils";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { AudioTagPicker } from "./AudioTagPicker";

const TagChip = ({
  label,
  node,
  editor,
  getPos,
}: NodeViewProps & { label: string }) => {
  const [value, setValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isFinishingRef = useRef(false);
  const isEditing = value !== null;

  useEffect(() => {
    const input = inputRef.current;
    if (!isEditing || !input) return;

    isFinishingRef.current = false;

    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [isEditing]);

  // `null` cancels the edit, an empty string removes the node
  const finishEditing = (text: string | null) => {
    // Refocusing the editor blurs the input, which would finish editing twice
    if (value === null || isFinishingRef.current) return;
    isFinishingRef.current = true;
    setValue(null);

    const from = getPos();
    if (from === undefined) return;
    const to = from + node.nodeSize;

    const newNode = text === null ? node : createTagNode(editor.schema, text);
    const chain = editor.chain().focus();

    if (!newNode) {
      chain.deleteRange({ from, to }).run();
    } else if (newNode.eq(node)) {
      chain.setTextSelection(to).run();
    } else {
      chain
        .insertContentAt({ from, to }, newNode.toJSON())
        .setTextSelection(from + newNode.nodeSize)
        .run();
    }
  };

  return (
    <NodeViewWrapper className="-mt-0.75 inline-flex align-middle select-none">
      <span
        className="flex h-5.5 cursor-pointer items-center rounded-md border border-tertiary-400/5 bg-tertiary-100 px-1.5 text-xs font-semibold text-tertiary-400"
        contentEditable={false}
        onClick={() => !isEditing && setValue(label)}
      >
        {isEditing ? (
          <span className="inline-grid">
            <span className="invisible col-start-1 row-start-1 whitespace-pre">
              {value || " "}
            </span>
            <input
              ref={inputRef}
              size={1}
              value={value}
              onChange={(e) =>
                setValue(e.target.value.replace(TAG_DISALLOWED_CHARS, ""))
              }
              onBlur={() => finishEditing(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  finishEditing(value);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  finishEditing(null);
                } else if (
                  e.key === "Backspace" &&
                  (e.metaKey || value === "")
                ) {
                  e.preventDefault();
                  finishEditing("");
                }
              }}
              className="col-start-1 row-start-1 w-full min-w-0 bg-transparent p-0 text-inherit outline-none [font:inherit]"
            />
          </span>
        ) : (
          label
        )}
      </span>
    </NodeViewWrapper>
  );
};

const AudioTagComponent = (props: NodeViewProps) => (
  <TagChip {...props} label={props.node.attrs.text as string} />
);

const TAG_DISALLOWED_CHARS = /[^\p{L}\p{N} '-]/gu;
const TYPED_TAG_REGEX = /\[([\p{L}\p{N} '-]+)\]$/u;
const PASTED_TAG_REGEX = /\[([\p{L}\p{N} '-]+)\]/gu;

const AudioTagExtension = Node.create({
  name: "audioTag",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      text: { default: "" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "audio-tag",
        getAttrs: (element) => ({ text: element.textContent ?? "" }),
      },
    ];
  },

  renderHTML({ node }) {
    return ["audio-tag", {}, node.attrs.text as string];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AudioTagComponent);
  },

  addInputRules() {
    return [
      new InputRule({
        find: TYPED_TAG_REGEX,
        handler: ({ state, range, match }) => {
          const node = createTagNode(state.schema, match[1]!);
          if (node) state.tr.replaceWith(range.from, range.to, node);
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      new PasteRule({
        find: PASTED_TAG_REGEX,
        handler: ({ state, range, match }) => {
          const node = createTagNode(state.schema, match[1]!);
          if (node) state.tr.replaceWith(range.from, range.to, node);
        },
      }),
    ];
  },
});

function createTagNode(schema: Editor["schema"], rawText: string) {
  const text = rawText.trim();
  if (!text) return null;

  return schema.nodes.audioTag!.create({ text });
}

type Props = {
  script: string;
  setScript: (script: string) => void;
  error?: string;
};

export default function ScriptEditor({ script, setScript, error }: Props) {
  const updateVoiceoverTabState = useEditorStore(
    (state) => state.service.updateVoiceoverTabState,
  );
  const voiceOverTabState = useEditorStore((state) => state.voiceoverTabState);
  const lastEmittedScriptRef = useRef(script);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        code: false,
        heading: false,
        blockquote: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        link: false,
        horizontalRule: false,
        hardBreak: false,
      }),
      AudioTagExtension,
    ],
    content: script,
    editorProps: {
      attributes: {
        class:
          "h-full w-full resize-none text-base outline-none min-h-[200px] leading-6.5",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      const trimmedHtml = html.replace(/^<p>\s+/, "<p>");

      if (html !== trimmedHtml) {
        const { from, to } = editor.state.selection;
        editor.commands.setContent(trimmedHtml);
        editor.commands.setTextSelection({ from, to });
      } else {
        lastEmittedScriptRef.current = html;
        setScript(html);
      }
    },
  });

  useEffect(() => {
    if (!editor || script === lastEmittedScriptRef.current) return;
    lastEmittedScriptRef.current = script;

    const currentContent = editor.getHTML();

    if (script !== currentContent) {
      const htmlContent = script.startsWith("<p>")
        ? script
        : `<p>${script}</p>`;

      // Use chain with focus to ensure proper state update and history tracking
      editor.chain().focus().clearContent().insertContent(htmlContent).run();
    }
  }, [script, editor]);

  const addAudioTag = useCallback(
    (text: string) => {
      if (editor) {
        insertInlineNode(editor, { type: "audioTag", attrs: { text } });
      }
    },
    [editor],
  );

  const handleImportToScriptWriter = useCallback(() => {
    if (editor) {
      const plainText = editor.getText().trim();

      if (plainText) {
        updateVoiceoverTabState((state) => {
          state.scriptWriterInput = plainText;
        });
      }
    }
  }, [editor, updateVoiceoverTabState]);

  const toggleScriptWriter = () => {
    updateVoiceoverTabState((state) => {
      state.view =
        voiceOverTabState.view === "SCRIPT_WRITER"
          ? "CREATE_VOICEOVER"
          : "SCRIPT_WRITER";
    });
  };

  const isScriptWriterOpen = voiceOverTabState.view === "SCRIPT_WRITER";
  const isEmpty = !script || script.trim() === "<p></p>";

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          "relative flex w-full flex-col rounded-md border bg-transparent px-4 py-3",
          error ? "border-destructive bg-destructive/2" : "border-input",
        )}
      >
        <div className="relative h-52 grow">
          {isEmpty && (
            <div
              className="pointer-events-none absolute top-0 left-0 z-10 text-base leading-6.5 text-muted-foreground"
              onClick={() => editor?.chain().focus().run()}
            >
              Generate a script using the{" "}
              <button
                type="button"
                className="pointer-events-auto relative z-20 cursor-pointer bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text font-semibold text-transparent underline decoration-primary-400 decoration-dashed underline-offset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleScriptWriter();
                }}
              >
                Scriptwriter
              </button>{" "}
              or write it manually...
            </div>
          )}
          <EditorContent editor={editor} className="h-full overflow-y-auto" />
        </div>
        <div className="mt-2 flex items-center gap-2 pt-2">
          <div className="flex">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="iconSm"
                  variant="quaternary"
                  onClick={() => addAudioTag("pause")}
                  type="button"
                  className="rounded-r-none"
                >
                  <IconClockHour5 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Pause</TooltipContent>
            </Tooltip>
            <AudioTagPicker
              onSelect={addAudioTag}
              className="rounded-l-none border-l-0"
            />
          </div>
          <Button
            size="sm"
            variant={
              voiceOverTabState.view === "SCRIPT_WRITER"
                ? "gradientBorder"
                : "quaternary"
            }
            icon={<IconSparkles width={24} height={24} />}
            iconPosition="leading"
            onClick={toggleScriptWriter}
          >
            Scriptwriter
          </Button>
          {isScriptWriterOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="iconSm"
                  variant="quaternary"
                  onClick={handleImportToScriptWriter}
                  disabled={!editor?.getText().trim()}
                  type="button"
                >
                  <IconFileArrowRight className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Paste to Scriptwriter</TooltipContent>
            </Tooltip>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="iconSm"
                  variant="quaternary"
                  onClick={() => editor?.chain().focus().undo().run()}
                  disabled={!editor?.can().undo()}
                  type="button"
                >
                  <IconArrowBackUp className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Undo</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="iconSm"
                  variant="quaternary"
                  onClick={() => editor?.chain().focus().redo().run()}
                  disabled={!editor?.can().redo()}
                  type="button"
                >
                  <IconArrowForwardUp className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Redo</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      <p className={cn("text-sm text-destructive", !error && "invisible")}>
        {error ?? "\u00A0"}
      </p>
    </div>
  );
}

function insertInlineNode(editor: Editor, node: JSONContent) {
  const { from, to, $from } = editor.state.selection;
  const { doc } = editor.state;
  const textAfter = doc.textBetween(to, Math.min(doc.content.size, to + 1));

  const content: JSONContent[] = [];
  if ($from.parentOffset > 0 && doc.textBetween(from - 1, from) !== " ") {
    content.push({ type: "text", text: " " });
  }

  content.push(node);

  if (textAfter !== " ") {
    content.push({ type: "text", text: " " });
  }

  editor.chain().focus().insertContent(content).run();
}
